import * as React from "react";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, Button, IconButton, MenuItem, Select, TextField, Tooltip, Typography} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import {ExternalApiClient} from "../../../../model/ExternalApiClient";
import {getConfig} from "../../../../../Config";
import {getHttpConfig} from "../../../../../common/utils/HttpUtils";

interface EndpointTemplate {
    key: string;
    label: string;
    method: "GET" | "POST" | "PUT" | "DELETE";
    /** Relativní path uvnitř /raal-api/{slug}/. Použije se přesně. */
    path: string;
    /** Pro non-GET — výchozí JSON body, který admin upraví v textarea. */
    bodyTemplate?: string;
}

const TEMPLATES: EndpointTemplate[] = [
    {
        key: "freight-create",
        label: "POST /freight/offer — vytvoření nabídky přepravy",
        method: "POST",
        path: "freight/offer",
        bodyTemplate: JSON.stringify({
            id: null,
            from: {country: "CZ", zip: "10000", city: "PRAHA"},
            to: {country: "SK", zip: "81101", city: "BRATISLAVA"},
            dateFrom: "2026-05-15 08:00:00",
            dateTo: "2026-05-15 18:00:00",
            price: {value: 500, currency: "EUR"},
            bodyType: {tarpaulin: true},
            bodyFormat: {standard: true},
            vehicleLoadType: {behind: true},
            truckType: {semiTrailer: true},
            accessory: {},
            loadDimensions: {weight: 24, length: 13.6},
            additionalInformation: {pallet: 33, comment: "Test offer"},
            contact: {
                name: "Test Dispatcher",
                email: "test@example.com",
                phone: "+420724010090",
                mobile: "+420724010090"
            }
        }, null, 2)
    },
    {
        key: "freight-delete",
        label: "DELETE /freight/offer — smazání nabídky přepravy",
        method: "DELETE",
        path: "freight/offer",
        bodyTemplate: JSON.stringify({id: 12345}, null, 2)
    },
    {
        key: "freight-search",
        label: "GET /freight/prepravaview — vyhledávání přeprav",
        method: "GET",
        path: "freight/prepravaview?page=0&size=10"
    },
    {
        key: "vehicle-create",
        label: "POST /vehicle/offer — vytvoření nabídky vozidla",
        method: "POST",
        path: "vehicle/offer",
        bodyTemplate: JSON.stringify({
            id: null,
            from: {country: "CZ", zip: "10000", city: "PRAHA"},
            to: {country: "SK", zip: "81101", city: "BRATISLAVA"},
            dateFrom: "2026-05-15 08:00:00",
            dateTo: "2026-05-15 18:00:00",
            price: {value: 500, currency: "EUR"},
            bodyType: {tarpaulin: true},
            bodyFormat: {standard: true},
            truckType: {semiTrailer: true},
            accessory: {},
            loadDimensions: {weight: 24, length: 13.6},
            additionalInformation: {comment: "Test vehicle offer"},
            contact: {
                name: "Test Dispatcher",
                email: "test@example.com",
                phone: "+420724010090",
                mobile: "+420724010090"
            }
        }, null, 2)
    },
    {
        key: "vehicle-delete",
        label: "DELETE /vehicle/offer — smazání nabídky vozidla",
        method: "DELETE",
        path: "vehicle/offer",
        bodyTemplate: JSON.stringify({id: 12345}, null, 2)
    },
    {
        key: "vehicle-search",
        label: "GET /vehicle/vozidloview — vyhledávání vozidel",
        method: "GET",
        path: "vehicle/vozidloview?page=0&size=10"
    }
];

interface ResponseState {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string;
    durationMs: number;
}

const statusColor = (status: number): string => {
    if (status >= 200 && status < 300) return "#2e7d32";
    if (status >= 400 && status < 500) return "#ed6c02";
    if (status >= 500) return "#d32f2f";
    return "#666";
};

const formatJson = (raw: string): string => {
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        return raw;
    }
};

interface Props {
    client: ExternalApiClient;
}

export const ExternalApiTester: React.FC<Props> = ({client}) => {
    const {t} = useTranslation();
    const [selectedKey, setSelectedKey] = useState<string>(TEMPLATES[0].key);
    const selected = useMemo(() => TEMPLATES.find(e => e.key === selectedKey)!, [selectedKey]);
    const [bodyText, setBodyText] = useState<string>(selected.bodyTemplate ?? "");
    const [response, setResponse] = useState<ResponseState | null>(null);
    const [loading, setLoading] = useState(false);

    // Při změně endpointu nahraď body za novou šablonu.
    const onSelectChange = (key: string) => {
        setSelectedKey(key);
        const tpl = TEMPLATES.find(e => e.key === key);
        setBodyText(tpl?.bodyTemplate ?? "");
        setResponse(null);
    };

    // Backend běží typicky na jiném portu / hostu než frontend (CRA dev na :3000,
    // Spring Boot na :8080) — bereme backendUrl z runtime config.
    const backendOrigin = (getConfig().backendUrl ?? "").replace(/\/$/, "");
    const apiPath = `/raal-api/${client.slug}/${selected.path}`;
    const fullUrl = `${backendOrigin}${apiPath}`;

    const buildHeaders = (): Record<string, string> => {
        const cfg = getHttpConfig();
        return {
            "Content-Type": selected.method === "GET" ? "application/x-www-form-urlencoded" : "application/json",
            "X-Raal-Admin-Test": "true",
            "Accept": "application/json",
            ...(cfg.token ? {"Authorization": `Bearer ${cfg.token}`} : {}),
            ...(cfg.fingerprint ? {"fingerprint": cfg.fingerprint} : {})
        };
    };

    const handleRun = async () => {
        setLoading(true);
        const start = Date.now();
        try {
            const init: RequestInit = {
                method: selected.method,
                headers: buildHeaders()
                // credentials: "omit" — token jde explicitně v Authorization headeru,
                // session cookies nepotřebujeme. Backend má CORS allowedOrigins="*",
                // což s credentials:include browser odmítne (preflight wildcard rule).
            };
            if (selected.method !== "GET" && bodyText) {
                init.body = bodyText;
            }
            const r = await fetch(fullUrl, init);
            const text = await r.text();
            const headers: Record<string, string> = {};
            r.headers.forEach((v, k) => { headers[k] = v; });
            setResponse({
                status: r.status,
                statusText: r.statusText,
                headers,
                body: text,
                durationMs: Date.now() - start
            });
        } catch (e: any) {
            setResponse({
                status: 0,
                statusText: "Network error",
                headers: {},
                body: String(e?.message ?? e),
                durationMs: Date.now() - start
            });
        } finally {
            setLoading(false);
        }
    };

    const buildCurl = (): string => {
        const parts = [`curl -X ${selected.method} \\`];
        parts.push(`  '${fullUrl}' \\`);
        // Pro curl maskujeme token placeholderem — admin si ho dosadí, neukazujeme reálný.
        const headers = {...buildHeaders(), Authorization: "Bearer <YOUR-TOKEN>"};
        Object.entries(headers).forEach(([k, v]) => {
            parts.push(`  -H '${k}: ${v}' \\`);
        });
        if (selected.method !== "GET" && bodyText) {
            const escaped = bodyText.replace(/'/g, "'\\''");
            parts.push(`  -d '${escaped}'`);
        } else {
            parts[parts.length - 1] = parts[parts.length - 1].replace(/\s\\$/, "");
        }
        return parts.join("\n");
    };

    const copyCurl = async () => {
        try {
            await navigator.clipboard.writeText(buildCurl());
        } catch {
            // ignore — některé prohlížeče blokují clipboard mimo HTTPS
        }
    };

    // Tester sedí uvnitř většího CodeBookForm. Ten má na <form> onFocus/onBlur, který
    // se snaží volat addEventListener/removeEventListener na e.target. Material UI Select
    // ale v některých případech vystavuje syntetický target bez DOM API a parent Form na
    // něm padne. Zastavujeme propagaci focus/blur událostí na úrovni testeru.
    const stop = (e: React.FocusEvent) => e.stopPropagation();

    return (
        <Box
            style={{border: "1px solid #ccc", borderRadius: 4, padding: 16}}
            onFocusCapture={stop}
            onBlurCapture={stop}
        >
            <Typography variant="subtitle1" style={{marginBottom: 4}}>{t("ExternalApiClient.TestTitle")}</Typography>
            <Typography variant="body2" color="textSecondary" style={{marginBottom: 12}}>
                {t("ExternalApiClient.TestDescription")}
            </Typography>
            <Typography variant="caption" color="textSecondary" component="div" style={{marginBottom: 12, lineHeight: 1.4}}>
                {t("ExternalApiClient.TestCountryHint")}
            </Typography>

            <Box style={{display: "flex", gap: 8, alignItems: "center", marginBottom: 12}}>
                <Select
                    value={selectedKey}
                    onChange={(e) => onSelectChange(e.target.value as string)}
                    style={{minWidth: 420}}
                >
                    {TEMPLATES.map(tpl => (
                        <MenuItem key={tpl.key} value={tpl.key}>{tpl.label}</MenuItem>
                    ))}
                </Select>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon/>}
                    onClick={handleRun}
                    disabled={loading}
                >
                    {loading ? t("ExternalApiClient.TestRunning") : t("ExternalApiClient.TestSend")}
                </Button>
                <Tooltip title={t("ExternalApiClient.TestCopyCurl") as string}>
                    <IconButton size="small" onClick={copyCurl}><FileCopyIcon fontSize="small"/></IconButton>
                </Tooltip>
            </Box>

            {selected.method !== "GET" && (
                <TextField
                    label={t("ExternalApiClient.TestRequestBody")}
                    multiline
                    minRows={8}
                    maxRows={20}
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value)}
                    fullWidth
                    InputProps={{style: {fontFamily: "monospace", fontSize: 12}}}
                    style={{marginBottom: 12}}
                />
            )}

            <Box style={{display: "flex", gap: 16, marginTop: 8}}>
                <Box style={{flex: 1}}>
                    <Typography variant="subtitle2">{t("ExternalApiClient.TestRequest")}</Typography>
                    <pre style={{background: "#f5f5f5", padding: 8, fontSize: 11, maxHeight: 200, overflow: "auto", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all"}}>
{`${selected.method} ${fullUrl}\n` +
Object.entries({...buildHeaders(), Authorization: "Bearer ***"}).map(([k, v]) => `${k}: ${v}`).join("\n") +
(selected.method !== "GET" && bodyText ? "\n\n" + bodyText : "")}
                    </pre>
                </Box>
                <Box style={{flex: 1}}>
                    <Typography variant="subtitle2">{t("ExternalApiClient.TestResponse")}</Typography>
                    {!response && (
                        <Typography variant="body2" color="textSecondary" style={{padding: 8}}>
                            {t("ExternalApiClient.TestNoResponse")}
                        </Typography>
                    )}
                    {response && (
                        <pre style={{background: "#f5f5f5", padding: 8, fontSize: 11, maxHeight: 200, overflow: "auto", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all"}}>
<span style={{color: statusColor(response.status), fontWeight: "bold"}}>
{`HTTP ${response.status} ${response.statusText}`}{response.durationMs ? ` — ${response.durationMs}ms` : ""}
</span>{`
${Object.entries(response.headers).map(([k, v]) => `${k}: ${v}`).join("\n")}

${formatJson(response.body)}`}
                        </pre>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
